import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { Dialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n/functions';
import { Icon } from '../../../base/icons';
import { IconCheck, IconMic } from '../../../base/icons/svg';
import { showErrorNotification } from '../../../notifications';
import {
    closeCaptionsModal,
    enrollVoiceClone,
    selectClonedVoice,
    setRequestingSubtitles,
    setTranslatedVoiceEnabled
} from '../../actions.web';
import { CLONE_RECORDING_SECONDS } from '../../constants';
import { getPrimaryLanguageCode } from '../../functions.any';
import {
    getStoredCloneVoiceId,
    getTranslatedVoiceUnavailableReason,
    isCloneVoiceSelected
} from '../../functions.web';
import abstractLanguageSelectorDialog, {
    IAbstractLanguageSelectorDialogProps
} from '../AbstractLanguageSelectorDialog';

import CloneLevelMeter from './CloneLevelMeter.web';
import LanguageList from './LanguageList.web';

const INK = '#111827';
const MUTED = '#5B6472';
const ACCENT = '#2D4F86';
const BORDER = '#E5E7EB';
const RECORDING = '#C62828';

const useStyles = makeStyles()(() => {
    const outlineButton = {
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: 8,
        cursor: 'pointer',
        display: 'inline-flex',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        gap: 6,
        height: 36,
        padding: '0 14px',
        '&:focus-visible': {
            outline: '3px solid #8EA8D6',
            outlineOffset: 2
        }
    };

    return {
        content: {
            color: INK,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            paddingBottom: 8
        },
        card: {
            backgroundColor: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            padding: '0px'
        },
        cardHeader: {
            alignItems: 'center',
            display: 'flex',
            gap: 12,
            padding: 12
        },
        cardText: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            gap: 4,
            minWidth: 0
        },
        cardTitle: {
            color: INK,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '18px',
            margin: 0
        },
        cardTitleDisabled: {
            color: '#6B7280'
        },
        cardDesc: {
            color: MUTED,
            fontSize: 12,
            lineHeight: '17px',
            margin: 0
        },
        cardBody: {
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '0 12px 12px'
        },
        switchTrack: {
            alignItems: 'center',
            backgroundColor: '#9AA3B0',
            border: 0,
            borderRadius: 12,
            cursor: 'pointer',
            display: 'flex',
            flexShrink: 0,
            height: 24,
            justifyContent: 'flex-start',
            padding: 3,
            transition: 'background-color 150ms ease',
            width: 40,
            '&[aria-checked="true"]': {
                backgroundColor: ACCENT,
                justifyContent: 'flex-end'
            },
            '&:disabled': {
                backgroundColor: '#D5DAE1',
                cursor: 'not-allowed'
            },
            '&:focus-visible': {
                outline: '3px solid #8EA8D6',
                outlineOffset: 2
            }
        },
        switchKnob: {
            backgroundColor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
            height: 18,
            width: 18
        },
        preview: {
            backgroundColor: '#F4F6FA',
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '10px 12px'
        },
        previewLabel: {
            color: MUTED,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase'
        },
        previewText: {
            color: INK,
            fontSize: 13,
            lineHeight: '19px',
            margin: 0,
            overflowWrap: 'anywhere'
        },
        previewSpeaker: {
            color: ACCENT,
            fontWeight: 600
        },
        previewPlaceholder: {
            color: MUTED
        },
        voiceBox: {
            backgroundColor: '#F4F6FA',
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            margin: '0 12px 12px',
            padding: 12
        },
        voiceStatus: {
            alignItems: 'center',
            display: 'flex',
            gap: 10
        },
        voiceIcon: {
            alignItems: 'center',
            borderRadius: '50%',
            display: 'flex',
            flexShrink: 0,
            height: 32,
            justifyContent: 'center',
            width: 32,
            color: '#4b6790'

        },
        voiceIconIdle: {
            backgroundColor: '#E1E7F1',
            color: '#4b6790'
        },
        voiceIconRecording: {
            backgroundColor: '#FBE4E4',
            color: RECORDING
        },
        voiceIconReady: {
            backgroundColor: '#E3F1E7',
            color: '#1E6B3A'
        },
        eyebrow: {
            color: MUTED,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase'
        },
        voiceTitle: {
            color: INK,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: '18px'
        },
        voiceDesc: {
            color: '#4B5563',
            fontSize: 12,
            lineHeight: '17px',
            margin: 0
        },
        errorText: {
            color: '#B42318'
        },
        sampleSentence: {
            backgroundColor: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            color: INK,
            fontSize: 13,
            lineHeight: '19px',
            margin: 0,
            padding: '8px 10px'
        },
        progressRow: {
            alignItems: 'center',
            display: 'flex',
            gap: 10
        },
        recordingDot: {
            backgroundColor: RECORDING,
            borderRadius: '50%',
            flexShrink: 0,
            height: 10,
            width: 10
        },
        progressTrack: {
            backgroundColor: '#DDE2EA',
            borderRadius: 3,
            flexGrow: 1,
            height: 6,
            overflow: 'hidden'
        },
        progressFill: {
            backgroundColor: RECORDING,
            height: '100%',
            transition: 'width 1s linear'
        },
        progressLabel: {
            color: '#374151',
            fontSize: 12,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600
        },

        // CloneLevelMeter draws a level bar plus a hint; the recording progress bar already fills the bar's
        // place in this layout, so only the "too quiet / good level" hint is kept.
        meterTrack: {
            display: 'none'
        },
        meterFill: {},
        meterHint: {
            fontSize: 11,
            fontWeight: 500,
            margin: 0
        },
        actions: {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8
        },
        recordButton: {
            ...outlineButton,
            border: `1px solid ${ACCENT}`,
            color: ACCENT
        },
        recordDot: {
            backgroundColor: 'currentColor',
            borderRadius: '50%',
            height: 8,
            width: 8
        },
        voiceChoice: {
            ...outlineButton,
            border: '1px solid #C9CFD8',
            color: '#374151',
            '&[aria-checked="true"]': {
                backgroundColor: '#EAF0F9',
                borderColor: ACCENT,
                color: INK
            }
        }
    };
});

const LanguageSelectorDialog = (
        props: IAbstractLanguageSelectorDialogProps
) => {
    const { dispatch, language, listItems, onLanguageSelected, subtitles, t,
        translationEnabled, transcription, confSettings, isLocalModerator } = props;

    const { classes: styles } = useStyles();

    const translatedVoiceEnabled = useSelector(state => state['features/subtitles']._translatedVoiceEnabled);

    // Translated voice needs a target language that is different from the language being spoken *and* one the
    // text-to-speech provider can actually produce - see getTranslatedVoiceUnavailableReason.
    const translatedVoiceUnavailableReason = useSelector(getTranslatedVoiceUnavailableReason);
    const translatedVoiceAvailable = !translatedVoiceUnavailableReason;
    let translatedVoiceHint;

    if (translatedVoiceUnavailableReason === 'noLanguage') {
        translatedVoiceHint = t('transcribing.translatedAudioNeedsCaptions');
    } else if (translatedVoiceUnavailableReason) {
        translatedVoiceHint = t(`transcribing.translatedVoiceUnavailable.${translatedVoiceUnavailableReason}`);
    }

    const toggleTranslatedVoice = useCallback(() => {
        dispatch(setTranslatedVoiceEnabled(!translatedVoiceEnabled));
    }, [ dispatch, translatedVoiceEnabled ]);

    // --- Clone voice state ---
    const [ cloneRecordState, setCloneRecordState ] = useState('idle');
    const [ cloneStateMsg, setCloneStateMsg ] = useState('');
    const [ cloneSecondsLeft, setCloneSecondsLeft ] = useState(CLONE_RECORDING_SECONDS);
    const [ cloneStream, setCloneStream ] = useState(null);

    // Count the recording down so it can't be mistaken for a recording that has simply hung.
    useEffect(() => {
        if (cloneRecordState !== 'recording') {
            return undefined;
        }

        setCloneSecondsLeft(CLONE_RECORDING_SECONDS);
        const interval = setInterval(() => setCloneSecondsLeft(seconds => Math.max(0, seconds - 1)), 1000);

        return () => clearInterval(interval);
    }, [ cloneRecordState ]);

    // Seeded from local storage so the picker survives a reload and a rejoin: the clone lives in the Inworld
    // workspace, not in this tab, and re-recording to get the id back would make a second paid clone.
    const [ cloneVoiceId, setCloneVoiceId ] = useState(getStoredCloneVoiceId());
    const [ useClonedVoice, setUseClonedVoice ] = useState(isCloneVoiceSelected());

    const handleCloneStateChange = useCallback((state, msg = '', stream = null) => {
        setCloneRecordState(state);
        setCloneStateMsg(msg);
        setCloneStream(stream);

        if (state === 'done') {
            setCloneVoiceId(getStoredCloneVoiceId());
            setUseClonedVoice(true);
        }
    }, []);

    const pickVoice = useCallback(useClone => {
        const conference = APP?.conference?._room;
        const localId = conference?.myUserId?.();

        if (!localId) {
            return;
        }

        setUseClonedVoice(useClone);
        dispatch(selectClonedVoice(localId, useClone));
    }, [ dispatch ]);

    const startCloneEnroll = useCallback(() => {
        const conference = APP?.conference?._room;
        const localId = conference?.myUserId?.();

        if (!localId) {
            setCloneRecordState('error');
            setCloneStateMsg('localParticipantIdMissing');

            return;
        }

        dispatch(enrollVoiceClone(localId, handleCloneStateChange));
    }, [ dispatch, handleCloneStateChange ]);

    // A clone exists once per browser: re-recording would create a second paid voice in the Inworld workspace, so
    // once there is one the only choice left is which voice to use.
    let voiceState = cloneRecordState;

    if (cloneVoiceId && (cloneRecordState === 'idle' || cloneRecordState === 'done')) {
        voiceState = 'cloned';
    }

    const voiceTitle = () => {
        switch (voiceState) {
        case 'recording':
            return t('transcribing.yourVoiceRecording');
        case 'uploading':
            return t('transcribing.yourVoiceCreating');
        case 'done':
            return t('transcribing.yourVoiceOwn');
        case 'error':
            return t('transcribing.yourVoiceError');
        case 'cloned':
            return t(useClonedVoice ? 'transcribing.yourVoiceOwn' : 'transcribing.yourVoiceStandard');
        default:
            return t('transcribing.yourVoiceStandard');
        }
    };

    const cloneStatusText = () => {
        if (cloneRecordState === 'error') {
            if (cloneStateMsg === 'voiceModeNotAvailable') {
                return t('transcribing.cloneVoiceErrorVoiceModeNotAvailable');
            }

            if (cloneStateMsg === 'microphoneAccessDenied') {
                return t('transcribing.cloneVoiceErrorMicrophoneAccessDenied');
            }

            if (cloneStateMsg === 'localParticipantIdMissing') {
                return t('transcribing.cloneVoiceErrorLocalParticipantIdMissing');
            }

            if (cloneStateMsg === 'cloningNotConfigured') {
                return t('transcribing.cloneVoiceErrorNotConfigured');
            }

            return t('transcribing.cloneVoiceErrorGeneric', {
                error: cloneStateMsg || 'unknown'
            });
        }
        if (cloneRecordState === 'recording') {
            return t('transcribing.cloneVoiceReadAloud');
        }
        if (cloneRecordState === 'uploading') {
            return t('transcribing.cloneVoiceStatusUploading');
        }
        if (voiceState === 'cloned') {
            return t('transcribing.cloneVoiceStatusCloned');
        }
        if (cloneRecordState === 'done') {
            return t('transcribing.cloneVoiceStatusDone');
        }

        return t('transcribing.cloneVoiceIdleDesc');
    };

    const voiceIconClass = {
        cloned: useClonedVoice ? styles.voiceIconReady : styles.voiceIconIdle,
        done: styles.voiceIconReady,
        error: styles.voiceIconRecording,
        idle: styles.voiceIconIdle,
        recording: styles.voiceIconRecording,
        uploading: styles.voiceIconIdle
    }[voiceState];

    const toggleSwitch = () => {

        if (transcription?.enabled && Boolean(language) === false) {
            if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                if (isLocalModerator) {
                    dispatch(showErrorNotification(
                        {
                            descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                            titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                        }));
                }
            } else {
                dispatch(closeCaptionsModal(true));
                dispatch(setRequestingSubtitles(true, false, null));
            }
        } else {
            dispatch(setRequestingSubtitles(false, false, null));
        }
    };

    const captionsOn = Boolean(language);
    const languageName = language ? t(`languages:${getPrimaryLanguageCode(language)}`) : '';
    const recordedSeconds = CLONE_RECORDING_SECONDS - cloneSecondsLeft;

    // The newest line on screen, exactly as the captions show it: translated once the translation arrives.
    const transcriptMessages = useSelector(state => state['features/subtitles']._transcriptMessages);
    const lastMessage = [ ...transcriptMessages.values() ].filter(Boolean).pop();
    const latestCaption = lastMessage && {
        name: lastMessage.participant?.name,
        text: lastMessage.translated || lastMessage.final
            || `${lastMessage.stable || ''}${lastMessage.unstable || ''}`
    };

    return (
        <Dialog
            cancelDisabled = { false }
            disableFooter = { true }
            hideCloseButton = { false }
            isModal = { false }
            ok = {{ hidden: true }}
            titleKey = 'transcribing.ClosedCaptions'
            width = 'small'>
            <div className = { styles.content }>
                <section className = { styles.card }>
                    <div className = { styles.cardHeader }>
                        <div className = { styles.cardText }>
                            <h3
                                className = { styles.cardTitle }
                                id = 'cc-live-captions'>
                                { t('transcribing.liveCaptionsTitle') }
                            </h3>
                            <p className = { styles.cardDesc }>{ t('transcribing.liveCaptionsDesc') }</p>
                        </div>
                        <button
                            aria-checked = { captionsOn }
                            aria-labelledby = 'cc-live-captions'
                            className = { styles.switchTrack }
                            onClick = { toggleSwitch }
                            role = 'switch'
                            type = 'button'>
                            <span className = { styles.switchKnob } />
                        </button>
                    </div>
                    { captionsOn && (
                        <div className = { styles.cardBody }>
                            <LanguageList
                                items = { listItems }
                                language = { language }
                                onLanguageSelected = { onLanguageSelected }
                                selectedLanguage = { subtitles }
                                switchEnabled = { captionsOn }
                                t = { t }
                                translationEnabled = { translationEnabled } />
                            <div className = { styles.preview }>
                                <span className = { styles.previewLabel }>{ t('transcribing.captionPreview') }</span>
                                { latestCaption?.text
                                    ? <p className = { styles.previewText }>
                                        { latestCaption.name && <span className = { styles.previewSpeaker }>
                                            { `${latestCaption.name}: ` }
                                        </span> }
                                        { latestCaption.text }
                                    </p>
                                    : <p className = { `${styles.previewText} ${styles.previewPlaceholder}` }>
                                        { t('transcribing.captionPreviewEmpty', { language: languageName }) }
                                    </p> }
                            </div>
                        </div>
                    ) }
                </section>

                <section className = { styles.card }>
                    <div className = { styles.cardHeader }>
                        <div className = { styles.cardText }>
                            <h3
                                className = { `${styles.cardTitle} ${
                                    translatedVoiceAvailable ? '' : styles.cardTitleDisabled}` }
                                id = 'cc-translated-audio'>
                                { t('transcribing.translatedAudio') }
                            </h3>
                            <p className = { styles.cardDesc }>
                                { translatedVoiceHint
                                    || t('transcribing.translatedAudioDesc', { language: languageName }) }
                            </p>
                        </div>
                        <button
                            aria-checked = { translatedVoiceAvailable && translatedVoiceEnabled }
                            aria-labelledby = 'cc-translated-audio'
                            className = { styles.switchTrack }
                            disabled = { !translatedVoiceAvailable }
                            onClick = { toggleTranslatedVoice }
                            role = 'switch'
                            type = 'button'>
                            <span className = { styles.switchKnob } />
                        </button>
                    </div>

                    { /* Shown whatever this toggle says: it sets what OTHER listeners hear of you, not what you hear. */ }
                    <div className = { styles.voiceBox }>
                        <div className = { styles.voiceStatus }>
                            <div className = { `${styles.voiceIcon} ${voiceIconClass}` }>
                                <Icon
                                    color = 'currentColor'
                                    fillColor = '#4b6790'
                                    size = { 16 }
                                    src = { voiceIconClass === styles.voiceIconReady ? IconCheck : IconMic } />
                            </div>
                            <div className = { styles.cardText }>
                                <span className = { styles.eyebrow }>{ t('transcribing.yourVoiceLabel') }</span>
                                <span className = { styles.voiceTitle }>{ voiceTitle() }</span>
                            </div>
                        </div>
                        <p
                            aria-live = 'polite'
                            className = { `${styles.voiceDesc} ${voiceState === 'error' ? styles.errorText : ''}` }>
                            { cloneStatusText() }
                        </p>

                        { voiceState === 'recording' && <>
                            <blockquote className = { styles.sampleSentence }>
                                { t('transcribing.cloneVoiceSampleSentence') }
                            </blockquote>
                            <div className = { styles.progressRow }>
                                <span className = { styles.recordingDot } />
                                <div className = { styles.progressTrack }>
                                    <div
                                        className = { styles.progressFill }
                                        style = {{ width: `${100 * recordedSeconds / CLONE_RECORDING_SECONDS}%` }} />
                                </div>
                                <span className = { styles.progressLabel }>
                                    { `0:${String(recordedSeconds).padStart(2, '0')} / 0:${
                                        String(CLONE_RECORDING_SECONDS).padStart(2, '0')}` }
                                </span>
                            </div>
                            { cloneStream && <CloneLevelMeter
                                stream = { cloneStream }
                                styles = { styles }
                                t = { t } /> }
                        </> }

                        { voiceState === 'cloned' && (
                            <div
                                aria-label = { t('transcribing.voicePickerLabel') }
                                className = { styles.actions }
                                role = 'radiogroup'>
                                { [ false, true ].map(clone => (
                                    <button
                                        aria-checked = { useClonedVoice === clone }
                                        className = { styles.voiceChoice }
                                        key = { String(clone) }
                                        onClick = { () => pickVoice(clone) }
                                        role = 'radio'
                                        type = 'button'>
                                        { t(clone ? 'transcribing.voicePickerCloned'
                                            : 'transcribing.voicePickerDefault') }
                                    </button>
                                )) }
                            </div>
                        ) }

                        { (voiceState === 'idle' || voiceState === 'error') && (
                            <div className = { styles.actions }>
                                <button
                                    className = { styles.recordButton }
                                    onClick = { startCloneEnroll }
                                    type = 'button'>
                                    <span className = { styles.recordDot } />
                                    { t(voiceState === 'error'
                                        ? 'transcribing.cloneVoiceButtonRetry'
                                        : 'transcribing.cloneVoiceRecordButton') }
                                </button>
                            </div>
                        ) }
                    </div>
                </section>
            </div>
        </Dialog>
    );
};

/*
 * We apply AbstractLanguageSelector to fill in the AbstractProps common
 * to both the web and native implementations.
 */

export default translate(abstractLanguageSelectorDialog(LanguageSelectorDialog)
);
